import React, { useState, useEffect } from 'react';
import { Users, MapPin, X, Search, Home } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { useNavigate } from 'react-router-dom';

type SmallGroup = Database['public']['Tables']['small_groups']['Row'];

interface JoinGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: SmallGroup;
}

function JoinGroupModal({ isOpen, onClose, group }: JoinGroupModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const formatPhone = (value: string) => {
    // Remove tudo que não for número
    const numbers = value.replace(/\D/g, '');
    
    // Formata o número
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Você precisa estar logado para enviar uma solicitação');

      // Remove formatação do telefone antes de enviar
      const phoneClean = phone.replace(/\D/g, '');

      const { error: submitError } = await supabase
        .from('group_requests')
        .insert([
          {
            group_id: group.id,
            name,
            email,
            phone: phoneClean,
            message,
            status: 'pending',
            user_id: session.user.id
          }
        ]);

      if (submitError) throw submitError;

      alert('Solicitação enviada com sucesso! O líder do grupo entrará em contato.');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative shadow-elevation-3">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-brand-teal">Participar do Grupo</h2>
          <p className="text-gray-600 mt-2">{group.name}</p>
          <p className="text-sm text-gray-500">Líder: {group.leader}</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Nome
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              placeholder="Digite seu nome completo"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              placeholder="seu@email.com"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Seu WhatsApp
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              placeholder="(00) 00000-0000"
              required
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Mensagem (opcional)
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
              placeholder="Deixe uma mensagem para o líder do grupo..."
              rows={4}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-teal text-white py-3 px-4 rounded-xl hover:bg-brand-teal/90 transition-colors disabled:opacity-50 shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
          >
            {loading ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        </form>
      </div>
    </div>
  );
}

function getGoogleMapsUrl(coordinates: string) {
  if (!coordinates) return '';
  const [lat, lng] = coordinates.split(',').map(coord => coord.trim());
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function validateCoordinates(coords: string): boolean {
  const coordsRegex = /^-?\d+\.?\d*,-?\d+\.?\d*$/;
  if (!coordsRegex.test(coords)) return false;

  const [lat, lng] = coords.split(',').map(Number);
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

export default function SmallGroups() {
  const [groups, setGroups] = useState<SmallGroup[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<SmallGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<SmallGroup | null>(null);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'region'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    filterGroups();
  }, [searchTerm, filter, groups]);

  async function fetchGroups() {
    try {
      const { data, error } = await supabase
        .from('small_groups')
        .select('*')
        .order('name');

      if (error) throw error;
      setGroups(data || []);
      setFilteredGroups(data || []);
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
    } finally {
      setLoading(false);
    }
  }

  function filterGroups() {
    let filtered = groups;

    if (searchTerm) {
      filtered = filtered.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.leader.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filter === 'region') {
      // Agrupar por região
      const regions = filtered.reduce((acc, group) => {
        const region = group.location.split(',')[0].trim();
        if (!acc[region]) {
          acc[region] = [];
        }
        acc[region].push(group);
        return acc;
      }, {} as Record<string, SmallGroup[]>);

      filtered = Object.values(regions).flat();
    }

    setFilteredGroups(filtered);
  }

  const handleJoinGroup = (group: SmallGroup) => {
    setSelectedGroup(group);
    setIsJoinModalOpen(true);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Pequenos Grupos</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <div className="mb-8">
        <p className="text-gray-600 mb-6">
          Nossos pequenos grupos são uma oportunidade de crescer na fé, desenvolver relacionamentos
          significativos e experimentar a verdadeira comunhão cristã.
        </p>

        {/* Filtros e Busca */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, líder ou localização..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'region')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="all">Todos os grupos</option>
            <option value="region">Agrupar por região</option>
          </select>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {filteredGroups.map((group) => (
          <div key={group.id} className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
            <h3 className="text-xl font-semibold text-brand-teal mb-4">{group.name}</h3>
            <div className="space-y-2 text-gray-600">
              <p><strong>Líder:</strong> {group.leader}</p>
              <p><strong>Horário:</strong> {group.schedule}</p>
              <div className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-brand-teal shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Local:</p>
                  <p>{group.location}</p>
                  {group.maps_url && validateCoordinates(group.maps_url) && (
                    <a
                      href={getGoogleMapsUrl(group.maps_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-teal hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      <MapPin className="h-4 w-4" />
                      Ver no Google Maps
                    </a>
                  )}
                </div>
              </div>
              {group.description && <p>{group.description}</p>}
            </div>
            <button
              onClick={() => handleJoinGroup(group)}
              className="mt-4 w-full bg-brand-teal text-white px-6 py-3 rounded-xl hover:bg-brand-teal/90 transition-colors shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
            >
              Participar deste grupo
            </button>
          </div>
        ))}
      </div>

      {filteredGroups.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm">
          {searchTerm ? (
            <p>Nenhum grupo encontrado para sua busca.</p>
          ) : (
            <p>Nenhum grupo disponível no momento.</p>
          )}
        </div>
      )}

      {selectedGroup && (
        <JoinGroupModal
          isOpen={isJoinModalOpen}
          onClose={() => setIsJoinModalOpen(false)}
          group={selectedGroup}
        />
      )}
    </div>
  );
}